import { Column } from "components/Layout/Grid";
import ManagementPanelModalCard from "components/Modal/ManagementPanelModalCard";
import Table from "components/Table";
import {
  statusProcessing,
} from "components/TablePortal/interface";
import toast from "components/Toast/dispatch";
import Parse from "html-react-parser";
import ErrorIcon from "img/error-img.svg";
import { toastNonstandardCode } from "pages/Quotes/Dashboard/utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ManagementPanelService from "services/managementPanel.service";
import QuoteServices from "services/quote.service";
import { RootState } from "store/ducks";
import {
  PendingItemsDetailDropdown,
  type IPendingItemDetailSubItem,
  type IPendingItemRow,
} from "./PendingItemsDetailDropdown";
import { pendingItemsTableModel } from "./pendingItemsTableModel";

const PAGE_SIZE = 10;

const MOCK_PENDING_ITEMS_RESPONSE = {
  data: [],
  total: 0,
};

function mapApiItemToPendingItemRow(item: any): IPendingItemRow {
  const productCode =
    item.productCode ??
    item.code ??
    item.codigo ??
    item.product?.code ??
    item.product?.codigo;
  const productDescription =
    item.productDescription ??
    item.description ??
    item.descricao ??
    item.product?.description ??
    item.product?.descricao;
  const brand =
    item.brand ??
    item.marca ??
    item.product?.brand ??
    item.manufacturer;

  const rawSubItems = item.subItems ?? item.quotations ?? item.cotacoes ?? item.items ?? item.itens ?? [];
  const subItems: IPendingItemDetailSubItem[] = Array.isArray(rawSubItems)
    ? rawSubItems.map((sub: any) => ({
        quotationId:
          sub.quotationId ??
          sub.quotation_id ??
          sub.cotacaoId ??
          sub.cotacao_id ??
          sub.id,
        codeQuotation:
          sub.codeQuotation ??
          sub.code ??
          sub.quotationCode ??
          sub.codigoCotacao ??
          sub.codigo,
        quotationCreatedAt:
          sub.createdAt ??
          sub.quotationCreatedAt ??
          sub.dataCriacao ??
          sub.created_at ??
          sub.creationDate,
        customerCode:
          sub.customerCode ??
          sub.buyer?.identifier ??
          sub.company?.crmCustomerCode ??
          sub.crmCustomerCode ??
          sub.cCliente,
        buyerName:
          sub.buyerName ??
          sub.buyer?.razaoSocial ??
          sub.buyer?.companyName ??
          sub.buyer?.name,
        category:
          sub.categoryMafra,
        salePrice:
          sub.salePrice ??
          sub.precoEnviado ??
          sub.price ??
          sub.preco,
        quotationOrigin: sub.quotationOrigin ?? sub.origem,
        status: sub.status,
        supplierStatus: sub.supplierStatus ?? sub.supplierStatusDescription,
      }))
    : [];

  return {
    quotationId:
      item.quotationId ??
      item.quotation_id ??
      item.cotacaoId ??
      item.cotacao_id,
    codeQuotation:
      item.codeQuotation ??
      item.quotationCode ??
      item.codigoCotacao,
    customerCode:
      item.customerCode ??
      item.buyer?.identifier ??
      item.company?.crmCustomerCode ??
      item.crmCustomerCode,
    status: item.status,
    supplierStatus: item.supplierStatus ?? item.supplierStatusDescription,
    quotationOrigin: item.quotationOrigin ?? item.origem,
    productCode,
    productDescription,
    brand,
    subItems,
  };
}

function pendingItemRowOriginLabel(row: IPendingItemRow): string {
  const o = row.quotationOrigin;
  if (o === 0 || o === "Bionexo") return "Bionexo";
  return "MVM";
}

export interface ModalPendingItemsProps {
  open: boolean;
  onClose: () => void;
  filterCurrent: Record<string, any>;
}

export const ModalPendingItems: React.FC<ModalPendingItemsProps> = ({
  open,
  onClose,
  filterCurrent,
}) => {
  const user = useSelector((state: RootState) => state.user);
  const quoteItemStorage = useSelector((state: RootState) => state.quoteItem) ?? { id: "" };
  const blockedSimultaneousAccess = process.env.REACT_APP_BLOCKED_SIMULTANEOUS_ACCESS;
  const bcgOnly = process.env.REACT_APP_BCG_ONLY === "true";

  const _managementPanelService = new ManagementPanelService();
  const _quotesServices = new QuoteServices();

  const searchValueRef = useRef("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");

  const [pendingItemsTableData, setPendingItemsTableData] = useState<IPendingItemRow[]>([]);
  const [totalPendingItemsCount, setTotalPendingItemsCount] = useState(0);
  const [loadingPendingItemsTable, setLoadingPendingItemsTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [tableOrder, setTableOrder] = useState("");

  const totalPages = Math.ceil(totalPendingItemsCount / PAGE_SIZE) || 1;

  const handleOpenQuotation = useCallback(
    async (row: IPendingItemRow) => {
      const id = row.quotationId;
      if (!id) {
        toast.info(
          "Cotação indisponível",
          "Não foi possível identificar a cotação para abrir o detalhe."
        );
        return;
      }

      const code = row.codeQuotation ?? "";
      if (code.length > 9) {
        toastNonstandardCode(pendingItemRowOriginLabel(row));
        return;
      }

      if (!row.customerCode) {
        toast.info(
          "Cliente não cadastrado",
          "Para continuar com a cotação, é necessário cadastrar o cliente antes."
        );
        return;
      }

      const isDisconsidered = row.supplierStatus === "DISREGARDED";
      if (isDisconsidered && (bcgOnly || user?.isBCGUser)) {
        toast.info("Cotação desconsiderada", "A cotação foi desconsiderada por condições comerciais.");
        return;
      }

      if (statusProcessing.includes(row.status ?? "")) {
        return;
      }

      const openInNewTab = (path: string) => {
        window.open(path, "_blank", "noopener,noreferrer");
      };

        try {
          const response = await _quotesServices.checkQuotationViewed(id);
          const users: string[] = response?.data?.data?.user ?? [];

          if (
            blockedSimultaneousAccess &&
            users?.length > 0 &&
            !users.includes(user?.fullName ?? "") &&
            quoteItemStorage
          ) {
            const description = Parse(
              `<p class='text-styles color-blue-gray mt-3 text-wrap text-center description-quote-blocked'>Essa cotação está sendo manipulada pelo colaborador <b>${users[0]}</b>. Aguarde a finalização para acessar as informações.</p>`
            );
            toast.errorCustom(
              "Importante!",
              description,
              ErrorIcon,
              true,
              "Fechar",
              19,
              14,
              true
            );
            return;
          }

          openInNewTab(`/gravado-no-money/${id}`);
        } catch {
          openInNewTab(`/cotacoes-nova/${id}`);
        }
    },
    [user, quoteItemStorage, blockedSimultaneousAccess, bcgOnly]
  );

  const pendingItemsModel = useMemo(() => pendingItemsTableModel(), []);

  async function loadPendingItems(page: number = 0, userEmail: string = selectedUserEmail) {
    setLoadingPendingItemsTable(true);
    try {
      const searchTerm = searchValueRef.current.trim();
      const trackedUser = userEmail.trim();
      const filterMapping = {
        ProductCategories: filterCurrent.ProductCategories,
        SellerCodes: filterCurrent.SellerCodes,
        Origin: filterCurrent.Origin,
        BusinessUnitIdentifier: filterCurrent.BusinessUnitIdentifier,
        StartDate: filterCurrent.StartDate,
        EndDate: filterCurrent.EndDate,
        Top: PAGE_SIZE,
        Skip: page * PAGE_SIZE,
        ...(searchTerm ? {
          Code: searchTerm,
          Description: searchTerm,
          BuyerName: searchTerm,
          CompanyCrmCustomerCode: searchTerm,
          Manufacturer: searchTerm,
        } : {}),
        ...(trackedUser ? { TrackedUser: trackedUser } : {}),
      };

      let response: any;
      try {
        response = await _managementPanelService.getDataInsightsRAItemsDetailsByStatus(
          0,
          filterMapping
        );
      } catch {
        response = MOCK_PENDING_ITEMS_RESPONSE;
      }

      const rawData = response?.data ?? response ?? [];
      const total = response?.total ?? (Array.isArray(rawData) ? rawData.length : 0);
      const rows: IPendingItemRow[] = Array.isArray(rawData)
        ? rawData.map((item: any) => mapApiItemToPendingItemRow(item))
        : [];

      const dataToUse = rows.length > 0 ? rows : MOCK_PENDING_ITEMS_RESPONSE.data.map((item: any) => mapApiItemToPendingItemRow(item));
      const totalToUse = rows.length > 0 ? (typeof total === "number" ? total : rows.length) : MOCK_PENDING_ITEMS_RESPONSE.total;

      setPendingItemsTableData(dataToUse);
      setTotalPendingItemsCount(totalToUse);
      setCurrentPage(page);
    } catch {
      const fallbackRows = MOCK_PENDING_ITEMS_RESPONSE.data.map((item: any) => mapApiItemToPendingItemRow(item));
      setPendingItemsTableData(fallbackRows);
      setTotalPendingItemsCount(MOCK_PENDING_ITEMS_RESPONSE.total);
    } finally {
      setLoadingPendingItemsTable(false);
    }
  }

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  function handleKeyDown(key: string) {
    if (key === "Enter") {
      setCurrentPage(0);
      loadPendingItems(0);
    }
  }

  function handlePageChange(newPage: number) {
    setCurrentPage(newPage);
    loadPendingItems(newPage);
  }

  if (!open) return null;

  return (
    <ManagementPanelModalCard
      title="Itens pendentes"
      searchFieldLabel="Buscar código do item, descrição ou marca"
      isLoading={loadingPendingItemsTable}
      onSearchSubmit={(value) => {
        searchValueRef.current = value;
      }}
      onCloseAction={onClose}
      onKeyDown={handleKeyDown}
      onOpen={() => {
        searchValueRef.current = "";
        setSelectedUserEmail("");
        setCurrentPage(0);
        loadPendingItems(0, "");
      }}
      onUserSelect={(selectedUser) => {
        setSelectedUserEmail(selectedUser.email);
        setCurrentPage(0);
        loadPendingItems(0, selectedUser.email);
      }}
      onUserClear={() => {
        setSelectedUserEmail("");
        setCurrentPage(0);
        loadPendingItems(0, "");
      }}
    >
      <Column className="w-100 w-lg-100 px-1 pl-sm-3 pl-md-3 pl-lg-3 mt-4">
        <Table
          trackingKey="Itens pendentes - Table"
          model={pendingItemsModel}
          data={pendingItemsTableData}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          setTableOrder={setTableOrder}
          tableOrder={tableOrder}
          showPagination={true}
          DropdownTemplate={PendingItemsDetailDropdown}
          customProps={{ onOpenQuotation: handleOpenQuotation }}
          portalCustomHeader
          className="w-100 lost-won-items-table lost-won-items-table--table pending-items-modal-table"
          emptyDataMessage="Nenhum item pendente encontrado"
        />
      </Column>
    </ManagementPanelModalCard>
  );
};
